import { Box, Flex, Icon, Image, Spinner, Text } from '@chakra-ui/react';
import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { FiChevronRight } from '@react-icons/all-files/fi/FiChevronRight';

import { useFilePicker } from '../../../../../../../hooks';
import { isUploadTodo } from '../utils';
import { TodoItem } from './useTodoList';

type TodoCardProps = {
  item: TodoItem;
  isProcessing: boolean;
  onUpload: (file: File) => void;
  onOpen?: () => void;
};

const SatisfiedRow = ({ item }: { item: TodoItem }) => (
  <Flex
    align="center"
    gap="10px"
    px="14px"
    py="9px"
    borderRadius="12px"
    borderWidth="1px"
    borderColor="platinum"
    bg="grayLightest"
    opacity={0.8}
  >
    <Flex
      flex="none"
      w="26px"
      h="26px"
      borderRadius="8px"
      align="center"
      justify="center"
      bg="leaseEndGreen"
      color="white"
    >
      <Icon as={FiCheck} boxSize="14px" />
    </Flex>
    <Box flex="1" minW={0}>
      <Text fontSize="13px" fontWeight={700} color="charcoal">
        {item.definition.label}
      </Text>
      {item.doneNote && (
        <Text fontSize="12px" color="grayMedium">
          {item.doneNote}
        </Text>
      )}
    </Box>
    {item.thumbnailUrl && (
      <Image
        src={item.thumbnailUrl}
        alt={item.definition.label}
        boxSize="32px"
        borderRadius="8px"
        objectFit="cover"
        flex="none"
      />
    )}
  </Flex>
);

const TodoCard = ({ item, isProcessing, onUpload, onOpen }: TodoCardProps) => {
  const { fileInputRef, openPicker, handleFileSelected } = useFilePicker(onUpload);

  if (item.state !== 'todo') return <SatisfiedRow item={item} />;

  const { definition } = item;
  const accept = isUploadTodo(definition) ? definition.accept : undefined;
  const handleTap = accept ? openPicker : onOpen;

  return (
    <>
      {accept && (
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          hidden
          onChange={handleFileSelected}
        />
      )}
      <Flex
        as="button"
        disabled={isProcessing}
        onClick={isProcessing ? undefined : handleTap}
        align="center"
        gap="13px"
        px="14px"
        py="13px"
        w="100%"
        textAlign="left"
        borderRadius="12px"
        borderWidth="1px"
        borderColor="platinum"
        bg="white"
        cursor={isProcessing ? 'default' : 'pointer'}
        _hover={isProcessing ? undefined : { borderColor: 'leaseEndBlue', bg: 'blackAlpha.50' }}
      >
        <Flex
          flex="none"
          w="36px"
          h="36px"
          borderRadius="9px"
          align="center"
          justify="center"
          bg="grayLightest"
          color="grayMedium"
          borderWidth="1px"
          borderColor="platinum"
        >
          <Icon as={definition.icon} boxSize="18px" />
        </Flex>
        <Box flex="1" minW={0}>
          <Text fontSize="14px" fontWeight={700} color="leaseEndBlue">
            {definition.label}
          </Text>
          <Text fontSize="12px" lineHeight="1.4" color="gray.600" mt="2px">
            {definition.help}
          </Text>
        </Box>
        <Box flex="none" color="grayMedium">
          {isProcessing ? (
            <Spinner size="sm" color="leaseEndBlue" />
          ) : (
            <Icon as={FiChevronRight} boxSize="18px" />
          )}
        </Box>
      </Flex>
    </>
  );
};

export default TodoCard;
